import { KeyboardEvent, useEffect, useMemo, useState } from 'react'
import { Download, FileSpreadsheet, FileText, Mail, X } from 'lucide-react'
import toast from 'react-hot-toast'
import { useAuth } from '../../hooks/useAuth'
import { apiService } from '../../services/api'
import { MaintenanceFilters } from '../../types/maintenance'
import Modal from '../shared/Modal'

type ExportFormat = 'xlsx' | 'pdf'
type ExportAction = 'download' | 'email'

interface ExportReportModalProps {
  isOpen: boolean
  onClose: () => void
  filters: MaintenanceFilters
}

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

export default function ExportReportModal({ isOpen, onClose, filters }: ExportReportModalProps) {
  const { user } = useAuth()
  const [format, setFormat] = useState<ExportFormat>('xlsx')
  const [action, setAction] = useState<ExportAction>('download')
  const [recipients, setRecipients] = useState<string[]>([])
  const [emailInput, setEmailInput] = useState('')
  const [sendCopy, setSendCopy] = useState(false)
  const [observations, setObservations] = useState('')
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (!isOpen) {
      setEmailInput('')
      setRecipients([])
      setSendCopy(false)
      setObservations('')
      setLoading(false)
    }
  }, [isOpen])

  const allRecipients = useMemo(() => {
    const values = [...recipients]
    if (sendCopy && user?.email) values.push(user.email.toLowerCase())
    return Array.from(new Set(values))
  }, [recipients, sendCopy, user?.email])

  const addEmail = (rawValue: string) => {
    const values = rawValue.split(/[,;]+/).map(value => value.trim().toLowerCase()).filter(Boolean)
    if (values.length === 0) return true
    const invalidEmail = values.find(value => !EMAIL_REGEX.test(value))
    if (invalidEmail) {
      toast.error(`E-mail inválido: ${invalidEmail}`)
      return false
    }
    setRecipients(current => Array.from(new Set([...current, ...values])))
    setEmailInput('')
    return true
  }

  const handleEmailKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter' || event.key === ',' || event.key === ';') {
      event.preventDefault()
      addEmail(emailInput)
    }
    if (event.key === 'Backspace' && !emailInput && recipients.length > 0) {
      setRecipients(current => current.slice(0, -1))
    }
  }

  const handleSubmit = async () => {
    if (action === 'email' && emailInput.trim() && !addEmail(emailInput)) return
    const effectiveRecipients = action === 'email'
      ? Array.from(new Set([...allRecipients, ...(emailInput.trim() && EMAIL_REGEX.test(emailInput.trim()) ? [emailInput.trim().toLowerCase()] : [])]))
      : []
    if (action === 'email' && effectiveRecipients.length === 0) {
      toast.error('Adicione pelo menos um destinatário')
      return
    }

    try {
      setLoading(true)
      if (action === 'download') {
        const { blob, filename } = await apiService.downloadMaintenanceReport(format, filters)
        const url = URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = filename
        document.body.appendChild(link)
        link.click()
        link.remove()
        URL.revokeObjectURL(url)
        toast.success('Relatório descarregado com sucesso')
      } else {
        await apiService.emailMaintenanceReport({
          format,
          recipients: effectiveRecipients,
          observations: observations.trim() || undefined,
          filters
        })
        toast.success('Relatório enviado por e-mail com sucesso')
      }
      onClose()
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Não foi possível exportar o relatório')
    } finally {
      setLoading(false)
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={loading ? () => undefined : onClose}
      size="lg"
      showCloseButton={false}
      title={<span className="flex items-center gap-2"><FileText className="h-5 w-5 text-brand-primary-500" />Exportar requisições</span>}
    >
      <div data-testid="export-modal" className="space-y-6 pt-2">
        <fieldset>
          <legend className="mb-3 text-sm font-semibold text-brand-text-primary">Formato</legend>
          <div className="grid grid-cols-2 gap-3">
            {(['xlsx', 'pdf'] as ExportFormat[]).map(value => (
              <label key={value} className={`flex cursor-pointer items-center gap-3 rounded-lg border p-4 ${format === value ? 'border-brand-primary-500 bg-brand-primary-50' : 'border-brand-neutral-300 bg-white'}`}>
                <input data-testid={`export-format-${value}`} type="radio" name="format" value={value} checked={format === value} onChange={() => setFormat(value)} className="h-4 w-4 text-brand-primary-500" />
                {value === 'xlsx' ? <FileSpreadsheet className="h-5 w-5 text-brand-primary-600" /> : <FileText className="h-5 w-5 text-brand-accent-500" />}
                <span className="text-sm font-medium">{value === 'xlsx' ? 'Excel (.xlsx)' : 'PDF'}</span>
              </label>
            ))}
          </div>
        </fieldset>

        <fieldset>
          <legend className="mb-3 text-sm font-semibold text-brand-text-primary">Ação</legend>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <label className={`flex cursor-pointer items-center gap-3 rounded-lg border p-4 ${action === 'download' ? 'border-brand-primary-500 bg-brand-primary-50' : 'border-brand-neutral-300 bg-white'}`}>
              <input data-testid="export-action-download" type="radio" name="action" checked={action === 'download'} onChange={() => setAction('download')} className="h-4 w-4 text-brand-primary-500" />
              <Download className="h-5 w-5" /><span className="text-sm font-medium">Baixar Arquivo</span>
            </label>
            <label className={`flex cursor-pointer items-center gap-3 rounded-lg border p-4 ${action === 'email' ? 'border-brand-primary-500 bg-brand-primary-50' : 'border-brand-neutral-300 bg-white'}`}>
              <input data-testid="export-action-email" type="radio" name="action" checked={action === 'email'} onChange={() => setAction('email')} className="h-4 w-4 text-brand-primary-500" />
              <Mail className="h-5 w-5" /><span className="text-sm font-medium">Enviar por E-mail</span>
            </label>
          </div>
        </fieldset>

        {action === 'email' && (
          <div className="space-y-4">
            <div>
              <label htmlFor="export-recipients" className="mb-2 block text-sm font-medium text-brand-text-primary">Destinatários</label>
              <div className="flex min-h-12 flex-wrap items-center gap-2 rounded-lg border border-brand-neutral-300 bg-white p-2 focus-within:ring-2 focus-within:ring-brand-primary-500">
                {recipients.map(email => (
                  <span key={email} className="inline-flex items-center gap-1 rounded-full bg-brand-primary-100 px-3 py-1 text-sm text-brand-primary-700">
                    {email}
                    <button data-testid={`export-remove-${email}`} type="button" aria-label={`Remover ${email}`} onClick={() => setRecipients(current => current.filter(value => value !== email))}><X className="h-3.5 w-3.5" /></button>
                  </span>
                ))}
                <input id="export-recipients" data-testid="export-recipients" value={emailInput} onChange={event => setEmailInput(event.target.value)} onKeyDown={handleEmailKeyDown} onBlur={() => emailInput.trim() && addEmail(emailInput)} placeholder={recipients.length ? '' : 'email@exemplo.pt'} className="min-w-48 flex-1 border-0 bg-transparent px-1 py-1 text-sm outline-none" />
              </div>
              <p className="mt-1 text-xs text-brand-text-muted">Prima Enter, vírgula ou ponto e vírgula para adicionar.</p>
            </div>
            <label className="flex items-center gap-2 text-sm text-brand-text-secondary">
              <input data-testid="export-send-copy" type="checkbox" checked={sendCopy} onChange={event => setSendCopy(event.target.checked)} disabled={!user?.email} className="h-4 w-4 rounded border-brand-neutral-300 text-brand-primary-500" />
              Enviar uma cópia para mim{user?.email ? ` (${user.email})` : ''}
            </label>
            <div>
              <label htmlFor="export-observations" className="mb-2 block text-sm font-medium text-brand-text-primary">Observações (opcional)</label>
              <textarea id="export-observations" data-testid="export-observations" value={observations} onChange={event => setObservations(event.target.value)} maxLength={2000} rows={4} className="input-field resize-y" placeholder="Adicione uma mensagem para os destinatários" />
            </div>
          </div>
        )}

        <div className="flex justify-end gap-3 border-t border-brand-neutral-300 pt-4">
          <button data-testid="export-cancel" type="button" onClick={onClose} disabled={loading} className="btn-neutral disabled:opacity-50">Cancelar</button>
          <button data-testid="export-submit" type="button" onClick={handleSubmit} disabled={loading} className="btn-primary inline-flex items-center gap-2 disabled:opacity-50">
            {action === 'download' ? <Download className="h-4 w-4" /> : <Mail className="h-4 w-4" />}
            {loading ? 'A processar...' : action === 'download' ? 'Baixar Arquivo' : 'Enviar por E-mail'}
          </button>
        </div>
      </div>
    </Modal>
  )
}
